import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PanelRightOpen, PanelRightClose } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface SplitCreationLayoutProps {
  /** Left panel — the chat / conversation UI */
  chatPanel: React.ReactNode;
  /** Right panel — the live preview / summary panel */
  previewPanel: React.ReactNode;
  /**
   * Default percentage of total width occupied by the chat panel.
   * Preview panel takes the remainder.  Defaults to 45.
   */
  defaultChatWidthPct?: number;
}

const CHAT_WIDTH_STEPS = [30, 35, 40, 45, 50, 55, 60] as const;
type ChatWidthStep = (typeof CHAT_WIDTH_STEPS)[number];

const snapToStep = (pct: number): ChatWidthStep => {
  return CHAT_WIDTH_STEPS.reduce((closest, step) =>
    Math.abs(step - pct) < Math.abs(closest - pct) ? step : closest
  ) as ChatWidthStep;
};

/**
 * SplitCreationLayout — two-column layout for the Chat Creation path.
 *
 * Left:  chat panel (ConversationSidebar or CelebrationChatCreation chat UI)
 * Right: live preview / summary panel (CreationPreviewPanel)
 *
 * Panels are draggable on desktop. On mobile the preview collapses behind a toggle.
 */
export const SplitCreationLayout: React.FC<SplitCreationLayoutProps> = ({
  chatPanel,
  previewPanel,
  defaultChatWidthPct = 45,
}) => {
  const [chatWidthPct, setChatWidthPct] = useState<ChatWidthStep>(
    snapToStep(defaultChatWidthPct)
  );
  const [isResizing, setIsResizing] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Detect desktop viewport
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Drag-to-resize handler
  const onDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const containerEl = containerRef.current;
      if (!containerEl) return;

      const startX = e.clientX;
      const startPct = chatWidthPct;
      const totalWidth = containerEl.getBoundingClientRect().width;

      const onMouseMove = (moveEvent: MouseEvent) => {
        const deltaPx = moveEvent.clientX - startX;
        const deltaPct = (deltaPx / totalWidth) * 100;
        const raw = startPct + deltaPct;
        const clamped = Math.min(65, Math.max(25, raw));
        setChatWidthPct(snapToStep(clamped));
      };

      const onMouseUp = () => {
        setIsResizing(false);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [chatWidthPct]
  );

  return (
    <div className="h-full w-full overflow-hidden py-4 px-4 flex flex-col">
      {/* Mobile: preview toggle bar */}
      <div className="lg:hidden flex items-center justify-end mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobilePreviewOpen((prev) => !prev)}
          className="gap-1.5 text-xs text-muted-foreground"
        >
          {mobilePreviewOpen ? (
            <>
              <PanelRightClose className="h-4 w-4" />
              Hide preview
            </>
          ) : (
            <>
              <PanelRightOpen className="h-4 w-4" />
              Show preview
            </>
          )}
        </Button>
      </div>

      {/* Mobile: preview panel (collapsible) */}
      {mobilePreviewOpen && (
        <div className="lg:hidden border rounded-lg mb-3 max-h-[40vh] overflow-y-auto">
          {previewPanel}
        </div>
      )}

      {/* Desktop: side-by-side with drag divider */}
      <div
        ref={containerRef}
        className={`flex-1 min-h-0 ${isDesktop ? 'grid' : 'flex flex-col'}`}
        style={
          isDesktop
            ? {
                gridTemplateColumns: `${chatWidthPct}% 0.5rem minmax(0, 1fr)`,
                cursor: isResizing ? 'col-resize' : 'default',
              }
            : undefined
        }
      >
        {/* Left: Chat Panel */}
        <div className="min-h-0 overflow-hidden">
          {chatPanel}
        </div>

        {/* Drag divider — desktop only */}
        {isDesktop && (
          <div
            className="hidden lg:flex items-center justify-center cursor-col-resize select-none"
            onMouseDown={onDividerMouseDown}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat and preview panels"
          >
            <div className="h-16 w-1 rounded-full bg-border hover:bg-primary/60 transition-colors" />
          </div>
        )}

        {/* Right: Preview Panel — desktop only */}
        {isDesktop && (
          <div className="hidden lg:block min-h-0 overflow-y-auto">
            {previewPanel}
          </div>
        )}
      </div>
    </div>
  );
};
